import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BehaviorSubject, Subject, Subscription, takeUntil, filter } from 'rxjs';

import { AnalyticsService } from 'app/shared/services/analytics.service';
import { ProfileService } from 'app/shared/services/profile.service';
import { TokensService } from '../../shared/services/tokens.service';
import { AuthService } from 'app/auth/auth.service';
import { ProfileStateService } from '../../shared/services/profile-state.service';

import { BillingInfoComponent } from './billing-info/billing-info.component';
import { CheckoutComponent } from '../payment/checkout/checkout.component';
import { ManageUserCredentialsComponent } from './manage-user-credentials/manage-user-credentials.component';
import { PreferencesComponent } from './preferences/preferences.component';
import { PurchasesComponent } from './purchases/purchases.component';
import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { SecurityComponent } from './security/security.component';
import { SupportComponent } from './support/support.component';
import { SubscriptionManagementComponent } from './subscription-management/subscription-management.component';
import { TransactionInfoComponent } from './subscription-management/transaction-info/transaction-info.component';
import { TokensCardComponent } from './tokens-card/tokens-card.component';
import { VerifyEmailComponent } from './verify-email/verify-email.component';
import { ToastComponent } from 'app/shared/components/toast/toast.component';

import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';
import {
  IBillingHistory,
  IBillingInfo,
  IUserProfile,
  IUserSubscription
} from 'app/interfaces/profile.interfaces';
import { IToastConfig } from 'app/shared/interfaces/toast-config.interfaces';

import { ESvgTypes } from 'app/shared/enums/svg.enums';
import {
  EProfileSectionTitle,
  EProfileState,
  EProfileTab,
  ENavigationSource,
  EProfileViewState
} from 'app/enums/profile.enums';
import { ELocalStorageKey } from 'app/shared/enums/shared.enums';
import { EToastType } from 'app/shared/enums/toast.enums';
import { ESubscriptionState } from '../../enums/purchases.enums';
import { EPaymentType } from '../../enums/payments.enums';

@Component({
  selector: 'stxt-profile-flyout',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    SecurityComponent,
    PreferencesComponent,
    PurchasesComponent,
    BillingInfoComponent,
    ManageUserCredentialsComponent,
    SupportComponent,
    SubscriptionManagementComponent,
    TransactionInfoComponent,
    VerifyEmailComponent,
    TokensCardComponent,
    ToastComponent,
    CheckoutComponent
  ],
  templateUrl: './profile-flyout.component.html',
  styleUrl: './profile-flyout.component.scss'
})
export class ProfileFlyoutComponent implements OnInit, OnDestroy {
  @Input() selectedTab: EProfileTab = EProfileTab.Account;
  @Input() getTokensScreen: boolean;
  @Output() closeDropdown: EventEmitter<string> = new EventEmitter<string>();
  public closeSvg: ISvgConfig = { fill: ESvgTypes.Tonal, name: 'close' };
  public backSvg: ISvgConfig = { fill: ESvgTypes.Tonal, name: 'arrow_left' };
  public credentialType: string;
  public isManageCredential: boolean = false;
  public isVerifyScreen: boolean = false;
  public showCheckoutComponent: boolean = false;
  public profileSettingsTitle$: BehaviorSubject<string> = this.profileService.profileActiveSection$;
  public profileUserData: IUserProfile;
  public billingInfo: IBillingInfo[] = [];
  public transactions: IBillingHistory[];
  public userSubscriptions: IUserSubscription[] = [];
  public selectedSubscription: IUserSubscription;
  public selectedTransaction: IBillingHistory;
  public titleValue: string;
  public subscriptionsLoaded: boolean = false;
  public analyticsTrackCompleted: boolean = false;
  public showTokensCheckoutComponent: boolean = false;
  public showSubscriptionCheckoutComponent: boolean = false;
  public EProfileTab = EProfileTab;
  public userId = localStorage.getItem('userId');
  public toast: IToastConfig = {
    toastHeading: 'Email address has been verified',
    toastType: EToastType.SUCCESS
  };
  public selectedSubscriptionData: IUserSubscription;
  public isToastMessage: boolean = false;
  public activeWalletTab: number = 1;
  public titleSubscription: Subscription;
  public navigationSource$ = new BehaviorSubject<ENavigationSource>(ENavigationSource.None);
  public currentViewState: EProfileViewState = EProfileViewState.MyProfile;
  protected readonly EProfileSectionTitle = EProfileSectionTitle;
  protected readonly EProfileState = EProfileState;
  protected readonly EPaymentType = EPaymentType;
  private readonly destroy$ = new Subject<void>();
  private authSubscription: Subscription;

  constructor(
    readonly profileService: ProfileService,
    readonly analyticsService: AnalyticsService,
    readonly tokensService: TokensService,
    private readonly profileStateService: ProfileStateService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.listenToAuthState();
    this.subscribeToProfileData();

    this.titleSubscription = this.profileSettingsTitle$.subscribe(res => (this.titleValue = res));

    if (!this.getTokensScreen) return;

    this.profileService.profileActiveSection$.next(EProfileSectionTitle.Checkout);
  }

  /**
   * Subscribe to prefetched profile data
   */
  subscribeToProfileData(): void {
    this.profileStateService.profileData$
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data)
      )
      .subscribe(data => {
        this.profileUserData = data;
        this.checkFirstTimeProfileOpen();
      });

    this.profileStateService.billingInfo$
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data)
      )
      .subscribe(data => {
        this.billingInfo = data;
        this.checkFirstTimeProfileOpen();
      });

    this.profileStateService.userSubscriptions$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.userSubscriptions = data || [];
      this.subscriptionsLoaded = true;
    });

    this.profileStateService.transactions$
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data)
      )
      .subscribe(data => {
        this.transactions = data.transactions;
      });
  }

  listenToAuthState(): void {
    this.authSubscription = this.authService.userLoaded$.subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.profileStateService.prefetchProfileData();
      }
    });
  }

  checkFirstTimeProfileOpen(): void {
    if (!this.profileUserData || !this.billingInfo) return;

    const profileOpened = localStorage.getItem(ELocalStorageKey.ProfileOpened);
    const isProfileComplete = !!(
      this.profileUserData?.email_verified &&
      this.profileUserData?.gender &&
      this.profileUserData?.interests &&
      this.billingInfo?.length
    );
    if (profileOpened || isProfileComplete) return;
    localStorage.setItem(ELocalStorageKey.ProfileOpened, 'true');
    this.analyticsService.onboardingProfile(EProfileState.PROFILE_STARTED, this.userId);
  }

  updateProfileData(): void {
    this.profileStateService.refreshProfileData();
  }

  openManageCredentials(credentialType: string): void {
    this.isManageCredential = true;
    this.credentialType = credentialType;
    this.profileService.profileActiveSection$.next(`Manage ${credentialType}`);
  }

  openVerifyScreen(): void {
    this.isVerifyScreen = true;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.Verify_Email);
  }

  closeSettings(): void {
    if (
      this.isManageCredential ||
      this.isVerifyScreen ||
      this.currentViewState !== EProfileViewState.MyProfile ||
      this.showTokensCheckoutComponent ||
      this.getTokensScreen
    ) {
      this.isManageCredential = false;
      this.isVerifyScreen = false;
      this.currentViewState = EProfileViewState.MyProfile;
      this.navigationSource$.next(ENavigationSource.None);
      this.activeWalletTab = 1;
      this.showTokensCheckoutComponent = false;
      this.showCheckoutComponent = false;
      this.getTokensScreen = false;
      this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);
      return;
    }

    this.closeDropdown.emit();
  }

  checkProfileStepCompleted(): void {
    this.closeDropdown.emit();

    if (!this.profileUserData || !this.billingInfo) return;

    const isProfileComplete = !!(
      this.profileUserData?.email_verified &&
      this.profileUserData?.gender &&
      this.profileUserData?.interests &&
      this.billingInfo?.length
    );

    if (!isProfileComplete || this.analyticsTrackCompleted) return;

    this.analyticsTrackCompleted = true;
    this.analyticsService.onboardingProfile(EProfileState.PROFILE_COMPLETE, this.userId);
    this.closeDropdown.emit(EProfileState.PROFILE_COMPLETE);
  }

  checkManageCredentialsPart(): void {
    if (this.profileUserData?.email_verified) {
      this.checkProfileStepCompleted();
      return;
    }

    this.isToastMessage = true;
    this.updateProfileData();
    this.checkProfileStepCompleted();
  }

  updateSubscription(): void {
    this.profileStateService.refreshUserSubscriptions();
  }

  fetchBillingInfo(): void {
    this.profileStateService.refreshBillingInfo();
    this.checkProfileStepCompleted();
  }

  openSubscriptionManagement(subscription: IUserSubscription): void {
    if (subscription.state === ESubscriptionState.Expired) return;
    this.selectedSubscription = subscription;

    if (this.currentViewState === EProfileViewState.ViewAllSubscriptions) {
      this.navigationSource$.next(ENavigationSource.ViewAllSubscriptionsToManagement);
    }

    this.currentViewState = EProfileViewState.ManageSubscription;
    this.activeWalletTab = 2;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_Subscription_Info);
  }

  openTransactionInfo(purchase: IBillingHistory): void {
    this.selectedTransaction = purchase;

    if (this.currentViewState === EProfileViewState.ViewAllPurchases) {
      this.navigationSource$.next(ENavigationSource.ViewAllPurchases);
    } else if (this.currentViewState === EProfileViewState.ViewAllSubscriptions) {
      this.navigationSource$.next(ENavigationSource.ViewAllSubscriptions);
    }

    this.currentViewState = EProfileViewState.TransactionInfo;
    this.activeWalletTab = 1;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_Transaction_Info);
  }

  selectTab(tab: EProfileTab): void {
    this.selectedTab = tab;
    switch (tab) {
      case EProfileTab.Account:
        this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);
        break;
      case EProfileTab.Wallet:
        this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);
        break;
    }
  }

  showCheckout(
    showCheckoutComponent: boolean,
    paymentType: EPaymentType,
    item?: IUserSubscription
  ): void {
    if (paymentType === EPaymentType.Token) {
      this.showTokensCheckoutComponent = showCheckoutComponent;
    } else {
      this.selectedSubscriptionData = item;
      this.showSubscriptionCheckoutComponent = showCheckoutComponent;
    }
    showCheckoutComponent
      ? this.profileService.profileActiveSection$.next(EProfileSectionTitle.Checkout)
      : this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);
  }

  handleEmailVerified(): void {
    this.profileStateService.refreshProfileData();

    this.isVerifyScreen = false;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);

    this.toast = {
      toastHeading: 'Email address has been verified',
      toastSubheading: 'Your email has been successfully verified.',
      toastType: EToastType.SUCCESS
    };
    this.isToastMessage = true;

    setTimeout(() => {
      this.isToastMessage = false;
      this.checkProfileStepCompleted();
    }, 3000);
  }

  handleLogout(): void {
    this.profileStateService.clearProfileData();

    // Reset component state
    this.isManageCredential = false;
    this.isVerifyScreen = false;
    this.currentViewState = EProfileViewState.MyProfile;
    this.navigationSource$.next(ENavigationSource.None);
    this.activeWalletTab = 1;
    this.showTokensCheckoutComponent = false;
    this.isToastMessage = false;
    this.subscriptionsLoaded = false;
    this.analyticsTrackCompleted = false;

    // Clear all data
    this.profileUserData = null;
    this.billingInfo = null;
    this.transactions = null;
    this.userSubscriptions = [];
    this.selectedSubscription = null;
    this.selectedTransaction = null;
    this.selectedTab = EProfileTab.Account;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);
    this.closeDropdown.emit(EProfileState.PROFILE_LOGOUT);
    localStorage.removeItem(ELocalStorageKey.VerifySent);
    localStorage.removeItem(ELocalStorageKey.RecentlyGeneratedImages);
  }

  openAllPurchases(): void {
    this.activeWalletTab = 1;
    this.currentViewState = EProfileViewState.ViewAllPurchases;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_All_Purchases);
  }

  openAllSubscriptions(): void {
    this.activeWalletTab = 2;
    this.currentViewState = EProfileViewState.ViewAllSubscriptions;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_All_Subscriptions);
  }

  private resetTransientStateAndNavigateToProfile(
    flag: 'isVerifyScreen' | 'isManageCredential'
  ): boolean {
    if (this[flag]) {
      this[flag] = false;
      this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);
      return true;
    }
    return false;
  }

  backFromAllPurchases(): void {
    if (this.resetTransientStateAndNavigateToProfile('isVerifyScreen')) return;
    if (this.resetTransientStateAndNavigateToProfile('isManageCredential')) return;

    const currentSource = this.navigationSource$.value;

    switch (this.currentViewState) {
      case EProfileViewState.TransactionInfo:
        this.handleTransactionInfoNavigation(currentSource);
        break;

      case EProfileViewState.ManageSubscription:
        this.handleManageSubscriptionNavigation(currentSource);
        break;

      case EProfileViewState.ViewAllPurchases:
      case EProfileViewState.ViewAllSubscriptions:
        this.navigateToMyProfile();
        break;

      default:
        this.navigateToMyProfile();
        break;
    }
  }

  private handleTransactionInfoNavigation(currentSource: ENavigationSource): void {
    if (currentSource === ENavigationSource.ViewAllPurchases) {
      this.currentViewState = EProfileViewState.ViewAllPurchases;
      this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_All_Purchases);
    } else if (currentSource === ENavigationSource.ViewAllSubscriptions) {
      this.currentViewState = EProfileViewState.ViewAllSubscriptions;
      this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_All_Subscriptions);
    } else {
      this.navigateToMyProfile();
    }
    this.navigationSource$.next(ENavigationSource.None);
  }

  private handleManageSubscriptionNavigation(currentSource: ENavigationSource): void {
    if (currentSource === ENavigationSource.ViewAllSubscriptionsToManagement) {
      this.currentViewState = EProfileViewState.ViewAllSubscriptions;
      this.profileService.profileActiveSection$.next(EProfileSectionTitle.View_All_Subscriptions);
      this.navigationSource$.next(ENavigationSource.None);
    } else {
      this.navigateToMyProfile();
    }
  }

  private navigateToMyProfile(): void {
    this.currentViewState = EProfileViewState.MyProfile;
    this.profileService.profileActiveSection$.next(EProfileSectionTitle.My_Profile);
  }

  get navIconConfig(): ISvgConfig {
    return this.currentViewState !== EProfileViewState.MyProfile ? this.backSvg : this.closeSvg;
  }

  handleNavIconClick(): void {
    if (this.currentViewState !== EProfileViewState.MyProfile) {
      this.backFromAllPurchases();
      return;
    }
    this.closeSettings();
  }

  get isMyProfile(): boolean {
    return this.currentViewState === EProfileViewState.MyProfile;
  }

  get isViewAllPurchases(): boolean {
    return this.currentViewState === EProfileViewState.ViewAllPurchases;
  }

  get isViewAllSubscriptions(): boolean {
    return this.currentViewState === EProfileViewState.ViewAllSubscriptions;
  }

  get isTransactionInfo(): boolean {
    return this.currentViewState === EProfileViewState.TransactionInfo;
  }

  get isManageSubscription(): boolean {
    return this.currentViewState === EProfileViewState.ManageSubscription;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.titleSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.tokensService.getMoreTokensEvent$.next(false);
  }
}
